import { useCallback, useEffect, useRef, useState } from 'react';
import { pmGet } from '../api';
import './ReportTemplates.css';
import { HelpPanel, type HelpContent } from '../../portfolio_definition/HelpPanel';
import { withFinalizedRule } from '../finalizedRuleHelp';

/**
 * Report Templates — reusable report starting points, stored in the platform database.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/roadmap-execution/report-templates.ejs`.
 *
 * THE SOURCE'S ONLY MATERIALIZE SCREEN in this module. It is built from Materialize's
 * `row`/`col s12 m6`/`card` classes rather than a hand-written grid, so the port reproduces
 * that layout with plain CSS at the SAME breakpoint (600px, Materialize's `m`) rather than
 * pulling Materialize into this SPA for one screen.
 *
 * UPLOAD IS FEATURE-FLAGGED. `templatesEnabled` comes from the server (`TEMPLATE_DECKS_ENABLED`,
 * or the presence of the deck-fill Lambda) and when it is off the form is dimmed, disabled
 * and captioned COMING SOON — with the source's own note pointing at Weekly Status / SteerCo,
 * which generate from live data and need no template.
 *
 * MULTI-FILE UPLOAD: the typed Name applies only when exactly ONE file is chosen; with
 * several, each file's own name is used. The caption says so, and the server enforces it.
 */

interface Template {
  id: number;
  slug: string;
  name: string;
  kind: string;
  icon: string;
  description: string | null;
  file_name: string | null;
  slide_count: number | null;
  bytes: number | null;
}

/** PowerPoint by extension, else the extension upper-cased, else just "File". */
function kindLabel(t: Template): string {
  if (t.file_name && /\.(pptx?|$)/i.test(t.file_name)) return 'PowerPoint';
  if (t.file_name) return (t.file_name.split('.').pop() ?? 'File').toUpperCase();
  return 'File';
}

/**
 * "How this works" — cloned from `ai_readiness_template/views/roadmap-execution/report-templates.ejs`,
 * its `rtHelp` object. Copy, colours and section order are the source's;
 * only the link hrefs are rewritten to this platform's routes, and the entities in
 * fields HelpPanel renders as TEXT (title, section label, term, link label) are
 * decoded, because only the HTML fields go through `dangerouslySetInnerHTML`.
 */
const RTPL_HELP: HelpContent = {
    title: 'Report Templates',
    icon: '📄',
    purpose: "A library of reusable <strong>PowerPoint templates</strong> for recurring delivery reporting, stored in the platform database. Templates are the starting point the <strong>Weekly Status</strong> and <strong>SteerCo</strong> screens use to generate decks.",
    doHere: [
      "<strong>Download</strong> a template to use or review it.",
      "<strong>Upload</strong> one or more of your own templates (PowerPoint, Word, Excel, PDF) — they're stored in the database and appear immediately.",
      "<strong>Delete</strong> templates you no longer need."
    ],
    fits: 'Report Templates &rarr; <strong>Weekly Status</strong> / <strong>SteerCo</strong> (generate a deck from a template)',
    firstTime: "Upload a branded deck, or use the seeded Steering Committee / Weekly Meeting templates, then open a reporting screen and pick it when generating a deck.",
    links: [
      { label: 'Weekly Status Meetings →', href: '/project_management/program-management/status-reporting/weekly-status-meetings' },
      { label: 'SteerCo Deck Creation →', href: '/project_management/program-management/status-reporting/steerco-deck-creation' }
    ]
  };

export function ReportTemplates() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const d = await pmGet<{ templates: Template[]; templatesEnabled: boolean }>(
        '/report-templates',
      );
      setTemplates(d.templates ?? []);
      setEnabled(d.templatesEnabled);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * A multipart POST, so it does NOT go through the JSON client — `fetch` with a `FormData`
   * body and no Content-Type header, letting the browser set the multipart boundary.
   */
  const upload = async (e: React.FormEvent) => {
    e.preventDefault();
    const files = fileRef.current?.files;
    if (!files || !files.length) {
      setError('No file selected.');
      return;
    }
    const fd = new FormData();
    if (name.trim()) fd.append('name', name.trim());
    if (description.trim()) fd.append('description', description.trim());
    for (const f of Array.from(files)) fd.append('files', f);
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/project_management/report-templates/upload', {
        method: 'POST',
        credentials: 'same-origin',
        body: fd,
      });
      const d = (await res.json()) as { success: boolean; error?: string };
      if (!d.success) {
        setError(d.error ?? 'Upload failed');
      } else {
        setName('');
        setDescription('');
        if (fileRef.current) fileRef.current.value = '';
        await load();
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t: Template) => {
    if (!window.confirm(`Delete “${t.name}”? This cannot be undone.`)) return;
    try {
      const res = await fetch(
        `/api/project_management/report-template/${t.id}/delete`,
        { method: 'POST', credentials: 'same-origin' },
      );
      const d = (await res.json()) as { success: boolean; error?: string };
      if (!d.success) setError(d.error ?? 'Delete failed');
      else await load();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div className="pm-rtpl">
      <HelpPanel help={withFinalizedRule(RTPL_HELP)} />
      <h1 className="page-header__title">Report Templates</h1>
      <p className="pm-rtpl-lede">
        Reusable report starting points stored in the platform database. Download a template, upload
        your own, or delete ones you no longer need.
      </p>

      {error ? <div className="pm-rtpl-error">{error}</div> : null}

      {templates.length ? (
        <div className="pm-rtpl-row">
          {templates.map((t) => {
            const kb = t.bytes ? Math.round(t.bytes / 1024) : null;
            return (
              <div className="pm-rtpl-col" key={t.id}>
                <div className="pm-rtpl-card">
                  <div className="pm-rtpl-cardhead">
                    <span className="pm-rtpl-ic">
                      <i className="material-icons">{t.icon || 'description'}</i>
                    </span>
                    <h6>{t.name}</h6>
                  </div>
                  {t.description ? <p className="pm-rtpl-desc">{t.description}</p> : null}
                  <div className="pm-rtpl-meta">
                    {kindLabel(t)}
                    {t.slide_count ? ` · ${t.slide_count} slides` : ''}
                    {kb ? ` · ${kb} KB` : ''}
                  </div>
                  <div className="pm-rtpl-actions">
                    <a
                      className="pm-rtpl-btn"
                      href={`/api/project_management/report-template/${t.id}/download`}
                    >
                      <i className="material-icons">file_download</i>Download
                    </a>
                    <button
                      className="pm-rtpl-del"
                      title="Delete"
                      onClick={() => void remove(t)}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="pm-rtpl-card pm-rtpl-empty">
          <i className="material-icons" style={{ fontSize: 56, color: '#cfd8dc' }}>
            folder_open
          </i>
          <h6>No templates yet</h6>
          <p>Upload your first report template below.</p>
        </div>
      )}

      <div className="pm-rtpl-card" style={{ marginTop: 8, position: 'relative' }}>
        <div className="pm-rtpl-cardhead">
          <span className="pm-rtpl-ic">
            <i className="material-icons">upload_file</i>
          </span>
          <h6>Upload a template</h6>
          {!enabled ? <span className="pm-rtpl-soon">COMING SOON</span> : null}
        </div>
        {!enabled ? (
          <div className="pm-rtpl-notice">
            ⏳ Template upload is <strong>coming soon</strong> in this environment. In the meantime,
            generate decks from live program data on{' '}
            <a href="/project_management/program-management/status-reporting/weekly-status-meetings">Weekly Status</a> /{' '}
            <a href="/project_management/program-management/status-reporting/steerco-deck-creation">SteerCo</a> (no
            template needed).
          </div>
        ) : null}
        <form
          onSubmit={(e) => void upload(e)}
          style={enabled ? undefined : { opacity: 0.5, pointerEvents: 'none' }}
        >
          <div className="pm-rtpl-fields">
            <div style={{ flex: 1, minWidth: 200 }}>
              <label>Name (optional — single file only)</label>
              <input
                type="text"
                placeholder="e.g. Monthly Project Review"
                disabled={!enabled}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div style={{ flex: 1.2, minWidth: 200 }}>
              <label>Description (optional)</label>
              <input
                type="text"
                placeholder="Short description"
                disabled={!enabled}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div style={{ flex: 1.3, minWidth: 240 }}>
              <label>Files — one or more (PowerPoint, Word, Excel, PDF)</label>
              <input
                ref={fileRef}
                type="file"
                name="files"
                multiple
                accept=".pptx,.ppt,.docx,.doc,.xlsx,.xls,.pdf"
                required={enabled}
                disabled={!enabled}
              />
            </div>
            <button
              type="submit"
              className="pm-rtpl-btn"
              disabled={!enabled || busy}
              style={{ background: enabled ? '#4a5159' : '#cfd8e3' }}
            >
              <i className="material-icons">file_upload</i>
              {busy ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </form>
        <p className="pm-rtpl-foot">
          Files are stored in the database (max 40&nbsp;MB each) and appear above immediately. Select
          several files to add them all at once — each file&apos;s name is used as its template name.
        </p>
      </div>
    </div>
  );
}
